// Anivaze v2 — Visual style identifiers (shared across API + runtime).
// See design-plan/v2/02-style-engine.md. Style is REFERENCED, never baked into
// the Flow/SceneGraph — that's what makes restyling free.

export type VisualStyle =
  | "studio" // default — the v1 dark look, kept so existing users see no change
  | "whiteboard"
  | "watercolour"
  | "retro"
  | "kawaii" // character tier — not yet available (Phase 5)
  | "anime"; // character tier — not yet available (Phase 5)

export interface VisualStyleInfo {
  id: VisualStyle;
  label: string;
  /** "filter" = re-skin via palette + SVG filters (cheap). "character" = needs art assets. */
  tier: "filter" | "character";
  /** Whether the style is shippable today. Character-tier styles show as "coming soon". */
  available: boolean;
  description: string;
}

/** Ordered for display in the "Choose visual style" picker. */
export const VISUAL_STYLES: VisualStyleInfo[] = [
  { id: "studio", label: "Studio", tier: "filter", available: true, description: "Clean dark vector look (default)" },
  { id: "whiteboard", label: "Whiteboard", tier: "filter", available: true, description: "Hand-drawn marker on paper" },
  { id: "watercolour", label: "Watercolour", tier: "filter", available: true, description: "Soft washes on cold-press paper" },
  { id: "retro", label: "Retro print", tier: "filter", available: true, description: "Risograph / halftone print" },
  { id: "kawaii", label: "Kawaii", tier: "character", available: false, description: "Cute character art (coming soon)" },
  { id: "anime", label: "Anime", tier: "character", available: false, description: "Cel-shaded anime (coming soon)" },
];

export const VISUAL_STYLE_IDS: VisualStyle[] = VISUAL_STYLES.map((s) => s.id);

export const DEFAULT_VISUAL_STYLE: VisualStyle = "studio";

export function isVisualStyle(value: unknown): value is VisualStyle {
  return typeof value === "string" && (VISUAL_STYLE_IDS as string[]).includes(value);
}

/** Whether a style can be used now (available + valid). Character-tier styles return false until Phase 5. */
export function isAvailableVisualStyle(value: unknown): value is VisualStyle {
  if (!isVisualStyle(value)) return false;
  return VISUAL_STYLES.find((s) => s.id === value)?.available ?? false;
}
